import {
    Amount,
    AuthContext,
    DataTable,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    RefundTransactionDetails,
    Transaction,
    TransactionTransferDetails,
} from '@enter/core';
import { mdiChevronDown, mdiChevronRight } from '@mdi/js';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore } from 'stores';
import * as S from './transactions.styles';

interface ITransactionsProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface ITransactionsState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<Transaction>[] = [
    {
        id: 'created',
        Header: 'Date',
        accessor: 'created',
        Cell: ({ cell }): React.ReactElement => <S.SelectableDiv selectable={true}>{cell.value}</S.SelectableDiv>,
    },
    {
        id: 'status',
        Header: 'Status',
        accessor: 'status',
        Cell: ({ cell }): React.ReactElement => <S.SelectableDiv selectable={true}>{cell.value}</S.SelectableDiv>,
    },
    {
        id: 'amount',
        Header: 'Amount',
        Cell: ({ cell }): React.ReactElement => (
            <S.SelectableDiv selectable={true}>
                ${(cell.row.original.amount && cell.row.original.amount.amount) || 0}
            </S.SelectableDiv>
        ),
    },
    {
        id: 'memo',
        Header: 'Memo',
        Cell: ({ cell }): React.ReactElement => (
            <S.SelectableDiv selectable={true}>{cell.row.original.memo}</S.SelectableDiv>
        ),
    },
    {
        id: 'selectable',
        maxWidth: 70,
        Cell: ({ cell }): React.ReactElement => {
            // FIXME: which signature includes functions from useExpanded?
            const path = cell.row.isExpanded ? mdiChevronDown : mdiChevronRight;
            return (
                <S.SelectableDiv selectable={true}>
                    <S.StyledIcon path={path} />
                </S.SelectableDiv>
            );
        },
    },
];

const filterSchemas: IFilterSchema<string>[] = [
    {
        label: 'Name',
        filterId: 'filterName',
        columnId: 'name',
        filterType: 'String',
    },
];

const formatAmount = (amt?: Amount): string => `$${(amt && amt.amount) || 0}`;
const formatTransferDetails = (ttd: TransactionTransferDetails | undefined): string => {
    if (!ttd) {
        return '';
    }
    return ttd.userId || ttd.legacyUserId || '';
};
const formatRefund = (rtd: RefundTransactionDetails): React.ReactElement => (
    <S.ExpandTable>
        <S.ExpandTBody>
            <S.ExpandTR>
                <S.ExpandTH>Refund Amount</S.ExpandTH>
                <S.ExpandTD>{formatAmount(rtd.amount)}</S.ExpandTD>
            </S.ExpandTR>
            <S.ExpandTR>
                <S.ExpandTH>Reimburse Amount</S.ExpandTH>
                <S.ExpandTD>{formatAmount(rtd.reimburseAmount)}</S.ExpandTD>
            </S.ExpandTR>
            <S.ExpandTR>
                <S.ExpandTH>Refund Memo</S.ExpandTH>
                <S.ExpandTD>{rtd.memo}</S.ExpandTD>
            </S.ExpandTR>
            <S.ExpandTR>
                <S.ExpandTH>Refund Status</S.ExpandTH>
                <S.ExpandTD>{rtd.status}</S.ExpandTD>
            </S.ExpandTR>
        </S.ExpandTBody>
    </S.ExpandTable>
);

class TransactionsPage extends React.Component<ITransactionsProps, ITransactionsState> {
    private presenter: DataTablePresenter<Transaction>;

    constructor(props: ITransactionsProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<Transaction>({
            name: 'guarantors',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'Provider Dashboard - Transactions';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('transactions');
    }

    public render(): React.ReactElement {
        return (
            <PageLayout>
                <PageHeader title="Transactions" hideDateRange={true} />
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={this.presenter}
                            isLoading={this.state.loading}
                            onFetch={this.onFetch}
                            onRowClick={this.onRowClick}
                            renderRowSubComponent={this.renderRowSubComponent}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({
                options: fetchOptions,
                loading: true,
            });
            this.authContext.cancelGroup('transactions');
            this.authContext.setGroup('transactions');

            const { pageIndex, pageSize, validFilters } = fetchOptions;
            const listing = await this.props.rootStore.transactionStore.loadTransactionListing({
                page: pageIndex,
                pageSize,
                filters: validFilters,
            });
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({
                loading: false,
            });
        } catch (err) {
            this.props.dialogContext.showError(err);
        }
    };

    private onRowClick = (row: Row<Transaction>): void => {
        row.toggleRowExpanded();
    };

    private renderRowSubComponent = (row: Row<Transaction>): React.ReactElement => {
        return (
            <S.Expand key={row.index}>
                <S.ExpandTable>
                    <S.ExpandTBody>
                        <S.ExpandTR>
                            <S.ExpandTH>From</S.ExpandTH>
                            <S.ExpandTD>{formatTransferDetails(row.original.from)}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>To</S.ExpandTH>
                            <S.ExpandTD>{formatTransferDetails(row.original.to)}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Billed By</S.ExpandTH>
                            <S.ExpandTD>{row.original.billedByDescriptor}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>chargedBankingId</S.ExpandTH>
                            <S.ExpandTD>{row.original.chargedBankingId}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Invoice ID</S.ExpandTH>
                            <S.ExpandTD>{row.original.invoiceId}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Subscription ID</S.ExpandTH>
                            <S.ExpandTD>{row.original.subscriptionId}</S.ExpandTD>
                        </S.ExpandTR>
                    </S.ExpandTBody>
                </S.ExpandTable>
                {row.original.refund && formatRefund(row.original.refund)}
            </S.Expand>
        );
    };
}

export const Transactions: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            return <TransactionsPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
        },
    ),
);
